import { z } from 'zod'
import { required } from '@portal/pkg-hook-form'
import { DashboardSubType, DashboardType } from '@portal/pkg-api/gen/admin-console'
import type { CriteriaRow } from './filterRestrictions'

/**
 * Type-aware validation for the add/edit drawer.
 *
 * Written as one flat object refined by `superRefine` rather than a discriminated union: the type
 * field is a live `watch` value, so the user can switch types mid-edit and a union would discard
 * what they had already typed. Keeping one shape lets the fields that still apply survive the
 * switch, and the refinement enforces per-type requirements at submit.
 */

const MAX_NAME = 255
const MAX_REPORT_ID = 36
const MAX_DATASET_ID = 255

/**
 * A row as `FilterCriteria` manages it. Passed through rather than re-modelled: that component owns
 * the row's shape and its own per-field validation, and the drawer only checks that a row is
 * complete enough to send.
 */
export const criteriaRowSchema = z.custom<CriteriaRow>()

/**
 * A "Required" issue on `path`.
 *
 * Emitted the same way `required()` from `@portal/pkg-hook-form` does — as an i18n key for the
 * zod-i18n map to resolve — rather than as a literal message. A literal renders untranslated and
 * lowercase next to the shared validator's "Required", which is how it first shipped.
 */
function requiredAt(ctx: z.RefinementCtx, path: (string | number)[]) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path,
    params: { i18n: { key: 'validator.required' } },
  })
}

export const DashboardSchema = z
  .object({
    name: z.string().superRefine(required()).pipe(z.string().max(MAX_NAME)),
    type: z.nativeEnum(DashboardType),
    reportId: z.string().max(MAX_REPORT_ID),
    subType: z.union([z.nativeEnum(DashboardSubType), z.literal('')]),
    datasetId: z.string().max(MAX_DATASET_ID),
    entityTypeId: z.union([z.number(), z.literal('')]),
    roleIds: z.array(z.number()),
    criteria: z.array(criteriaRowSchema),
  })
  .superRefine((values, ctx) => {
    // Required for every type: ACs 2, 3 and 4 all list "External Report ID (text, required)".
    // This is a client-side rule only -- the server allows a null reportId.
    if (!values.reportId.trim()) {
      requiredAt(ctx, ['reportId'])
    }
    if (values.roleIds.length === 0) {
      requiredAt(ctx, ['roleIds'])
    }
    if (values.type === DashboardType.INSIGHTS && !values.subType) {
      requiredAt(ctx, ['subType'])
    }
    // A paginated insights report is embedded from its dataset, so the server requires the id.
    // Note the labels read inverted against the enum: PAGINATED is the one shown as "Report".
    // Without this the form submits and comes back as a 400 with no field to point at.
    if (
      values.type === DashboardType.INSIGHTS &&
      values.subType === DashboardSubType.PAGINATED &&
      !values.datasetId.trim()
    ) {
      requiredAt(ctx, ['datasetId'])
    }
    if (values.type === DashboardType.SUMMARY && values.entityTypeId === '') {
      requiredAt(ctx, ['entityTypeId'])
    }
    if (values.type !== DashboardType.SUMMARY) return

    values.criteria.forEach((row, index) => {
      // A row with no field selected would send operand1 null, which the server rejects.
      if (row.operand1 === null) {
        requiredAt(ctx, ['criteria', index, 'operand1'])
      }
      if (!row.operator) {
        requiredAt(ctx, ['criteria', index, 'operator'])
      }
    })
  })

export type DashboardFormValues = z.infer<typeof DashboardSchema>

export const dashboardDefaults: DashboardFormValues = {
  name: '',
  type: DashboardType.HOMEPAGE,
  reportId: '',
  subType: '',
  datasetId: '',
  entityTypeId: '',
  roleIds: [],
  criteria: [],
}
